import { useCallback, useEffect, useMemo, useState } from "react";
import { useLanguage } from "../../../../stores/LanguageStore";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import numberColumn from "../../../../layouts/Table/columns/numberColumn";
import { useContactHandler } from "../../lists/components/useContactHandler";
import {
  findProspect,
  getFirstPagePrm,
  getFirstPagePrmShared,
  getFirstPagePrmUser,
  updateProspect,
} from "../../../../api/PRMAPI";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  useLeadsSheetStore,
  useLeadsStore,
} from "../../../../stores/LeadsStore";
import { ActionIcon, Flex, Indicator, Stack, Tooltip } from "@mantine/core";
import MainTable from "../../../../layouts/Table/MainTable";
import { notifications } from "@mantine/notifications";
import { getChangedKeys } from "../../lists/listUtils";
import { useConfigStore } from "../../../../stores/ConfigStore";
import { IconBell, IconMessage, IconUser } from "@tabler/icons-react";
import PrmCellStatus from "./cell/PrmCellStatus";
import { tagsColumn } from "../../../../layouts/Table/columns/tagsColumn";
import TableToolbar from "./TableToolbar";
import MoreActions from "../prospect/actions/MoreActions";
import ResumeWorkflow from "../prospect/actions/ResumeWorkflow";
import { IconLinkedin } from "../../../../components/Icons/IconLinkedin";
import { IconEmail } from "../../../../assets/icons/IconEmail";
import LeadsTableAdditionalToolbar from "./LeadsTableAdditionalToolbar";
import LeadsTableFilter from "./LeadsTableFilter";
import {
  transformFilterToServer,
  transformServerToFilter,
} from "../../../../functions/utilFunctions";
import PrmCellPositive from "./cell/PrmCellPositive";
import PrmCellCalls from "./cell/PrmCellCalls";
import { getCallColor, getDifference } from "../utils";
import { parseDate, parseDateFuture } from "../../../../functions/dates";

const LeadsMainTable = () => {
  const lang = useLanguage((s) => s.language);
  const apiRef = useGridApiRef();
  const { userId } = useParams();
  const sharedConditions = useLeadsStore((s) => s.sharedConditions);
  const filter = useLeadsStore((s) => s.filter);
  const setFilter = useLeadsStore((s) => s.setFilter);
  const [reloadColumns, setReloadColumns] = useState(true);
  const [tableFilter, setTableFilter] = useState(
    filter?.values.length > 0
      ? transformServerToFilter(filter)
      : {
          items: [],
          logicOperator: "and",
          quickFilterValues: [],
        },
  );

  // Columns
  const startingColumns = useMemo(
    () => [
      numberColumn(),
      {
        field: "status",
        id: "status",
        headerName: lang.prm.filter_labels.custom_status,
        sortable: true,
        width: 120,
        renderCell: ({ row, value }) => {
          return <PrmCellStatus row={row} apiRef={apiRef} />;
        },
      },
      tagsColumn({
        editFunction: () => updateProspect,
        sortable: false,
        apiRef,
      }),
      {
        field: "is_positive",
        id: "is_positive",
        headerName: lang.prm.filter_labels.is_positive,
        sortable: true,
        width: 120,
        renderCell: ({ row, value }) => {
          return <PrmCellPositive row={row} apiRef={apiRef} />;
        },
      },
      {
        field: "calls",
        id: "calls",
        headerName: lang.prm.details.calls,
        sortable: true,
        width: 150,
        renderCell: ({ row, value }) => {
          return <PrmCellCalls row={row} apiRef={apiRef} />;
        },
      },
      numberColumn("score", lang.prm.details.score, 100, { fontWeight: 600 }),
    ],
    [
      lang.prm.details.calls,
      lang.prm.filter_labels.custom_status,
      lang.prm.filter_labels.is_positive,
      lang.prm.details.score,
    ],
  );

  // API
  const {
    loading,
    columns,
    rows,
    rowCount,
    serverInfo,
    handleLoading,
    onPaginationChange,
    onSortChange,
  } = useContactHandler({
    apiRef,
    listId: userId,
    getFirstPage: userId
      ? getFirstPagePrmUser
      : sharedConditions
        ? getFirstPagePrmShared
        : getFirstPagePrm,
    searchFunction: ({ query, filter, ...data }) => {
      let main = userId
        ? getFirstPagePrmUser
        : sharedConditions
          ? getFirstPagePrmShared
          : getFirstPagePrm;

      return main({
        ...data,
        id: userId,
        filter: query
          ? {
              mode: "and",
              values: [
                {
                  field_name: "any_datafield",
                  type: "contains",
                  value: query,
                },
                ...(filter?.values || []),
              ],
            }
          : filter,
        conditions: sharedConditions?.map((x) => {
          return {
            ...x,
            ids_list:
              x?.ids_list?.map((x) => x.id) ||
              x?.sharing_targets?.map((x) => x.id),
          };
        }),
      });
    },
    reloadColumns,
    setReloadColumns,
    startingColumns,
    cellEditable: true,
    disableEdit: true,
  });

  const handleRowUpdate = useCallback(
    async (updatedRow, originalRow) => {
      const changedKey = getChangedKeys(updatedRow, originalRow);
      if (updatedRow[changedKey] !== originalRow[changedKey]) {
        let rowElement = document.querySelector(
          `[role="rowgroup"] [data-id="${originalRow.id}"]`,
        );

        rowElement.style.opacity = 0.3;

        updateProspect({
          id: originalRow?.id,
          properties: [
            {
              data_field_id: parseInt(changedKey),
              value: updatedRow[changedKey],
            },
          ],
        })
          .then(() => {
            findProspect(originalRow.id)
              .then((r) => {
                let newProfile = r.data.contact_profile;
                let result = {
                  id: newProfile.id,
                  ...newProfile,
                };
                const datafields = useConfigStore.getState().datafields;
                newProfile.properties.forEach((prop) => {
                  let field = datafields.find(
                    (x) => x.id === prop.data_field_id,
                  );

                  if (field) result[field.id] = prop.value;
                });

                apiRef.current.updateRows([result]);
              })
              .finally(() => {
                rowElement.style.opacity = 1;
              });
          })
          .catch((err) => {
            rowElement.style.opacity = 1;
            let cellElement = document.querySelector(
              `[role="rowgroup"] [data-id="${originalRow.id}"] > [data-field="${changedKey}"]`,
            );
            let linkElement = document.querySelector(
              `[role="rowgroup"] [data-id="${originalRow.id}"] > [data-field="${changedKey}"] a`,
            );
            let e = linkElement || cellElement;

            e.innerText = originalRow?.[changedKey] || "-";

            apiRef.current.updateRows([originalRow]);
          });

        return updatedRow;
      }

      return originalRow;
    },
    [apiRef],
  );

  const handleRowUpdateError = useCallback((err) => {
    return notifications.show({ message: "Contact non modifié", color: "red" });
  }, []);

  // Details
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialQuery = params.get("query");
  const initialFilter = params.get("filter");

  const handleShowDetail = useCallback(
    (row, tab = "summary") => {
      const setOpened = useLeadsSheetStore.getState().setOpened;
      const setIndex = useLeadsSheetStore.getState().setIndex;
      const setCurrent = useLeadsSheetStore.getState().setCurrent;
      const setActiveTab = useLeadsSheetStore.getState().setActiveTab;
      const allRowIds = apiRef.current.getAllRowIds();

      setOpened(true);
      setIndex(allRowIds.findIndex((id) => id === row.id));
      setCurrent(row);
      setActiveTab(tab);
      // Update URL so that on refresh, we reopen the lead sheet
      const searchParams = new URLSearchParams(location.search);
      searchParams.set("contact", row.id);
      searchParams.set(
        "index",
        allRowIds.findIndex((id) => id === row.id),
      );

      apiRef.current?.updateRows([
        { ...row, new_reply: false, new_first_reply: false },
      ]);

      navigate({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    },
    [apiRef, location.pathname, location.search, navigate],
  );

  const actionsCell = useCallback(
    ({ row, value }) => {
      return (
        <>
          <Tooltip label={lang.prm.table.view_detail}>
            <ActionIcon onClick={() => handleShowDetail(row)} variant="light">
              <IconUser size={18} />
            </ActionIcon>
          </Tooltip>

          <Tooltip
            label={
              getDifference(row?.calls[0]?.call_date).result < 0
                ? parseDate(row.calls[0]?.call_date, false)
                : parseDateFuture(row.calls[0]?.call_date)
            }
          >
            <Indicator
              color={getCallColor(row.calls[0]?.call_date, true)}
              inline
              offset={2}
              disabled={row?.calls.length === 0}
              styles={{
                indicator: {
                  width: 8,
                  height: 8,
                  minWidth: 0,
                },
              }}
              size={16}
              mx={4}
            >
              <ActionIcon
                variant={row?.calls.length === 0 ? "subtle" : "light"}
                color={getCallColor(row.calls[0]?.call_date, true)}
                className="lead-item-button"
                opacity={row?.calls.length === 0 ? undefined : 1}
                onClick={() => handleShowDetail(row, "reminders")}
              >
                <IconBell size={16} />
              </ActionIcon>
            </Indicator>
          </Tooltip>

          <Tooltip
            label={`${row?.number_of_replies || " 0 "} ${
              lang.statistics.responses_viewer.answer
            }${(row?.number_of_replies || 0) > 1 ? "s" : ""}`}
          >
            <Indicator
              color="red"
              inline
              offset={2}
              mx={4}
              disabled={row?.number_of_replies === 0}
              styles={{
                indicator: {
                  width: 8,
                  height: 8,
                  minWidth: 0,
                },
              }}
              size={16}
            >
              <ActionIcon
                variant={
                  row?.number_of_replies === 0 &&
                  row.last_reply_type !== "linkedin"
                    ? "subtle"
                    : "light"
                }
                color="primary"
                className="lead-item-button"
                onClick={() => handleShowDetail(row, "answers")}
              >
                {row?.number_of_replies === 0 ? (
                  <IconMessage size={18} />
                ) : row.last_reply_type === "linkedin" ? (
                  <IconLinkedin size={18} />
                ) : (
                  <IconEmail size={16} />
                )}
              </ActionIcon>
            </Indicator>
          </Tooltip>

          {/* <PositiveChanger
                    containerId={row?.custom_status || row.status}
                    is_positive={row?.is_positive}
                    itemId={row.id}
                    pos="static"
                /> */}

          {row.workflow_waiting_steps.length > 0 && (
            <ResumeWorkflow current={row} />
          )}

          <MoreActions
            current={row}
            container={row?.custom_status || row.status}
            inTable
            handleLoading={handleLoading}
          />
        </>
      );
    },
    [apiRef],
  );

  const toolbarActions = useCallback(
    ({ selectedIds, filter, handleClose }) => {
      return (
        <TableToolbar
          total={serverInfo?.number_of_results}
          rowCount={rowCount}
          filter={filter}
          handleClose={handleClose}
          selectedIds={selectedIds}
          handleLoading={handleLoading}
          apiRef={apiRef}
        />
      );
    },
    [rowCount],
  );

  useEffect(() => {
    if (apiRef.current && columns?.length > 0) {
      setFilter(transformFilterToServer(tableFilter));
      handleLoading(true);
    }
  }, [tableFilter, apiRef, columns?.length]);

  useEffect(() => {
    const setCurrentContainer =
      useLeadsSheetStore.getState().setCurrentContainer;

    setCurrentContainer({ items: rows });
  }, [rows]);

  if (!columns) {
    return;
  }

  return (
    <Stack h={"100%"}>
      <LeadsTableAdditionalToolbar
        apiRef={apiRef}
        tableFilter={tableFilter}
        setTableFilter={setTableFilter}
        loading={loading}
      />
      <Flex h={0} flex={1} mt={12}>
        <MainTable
          isClient={false}
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          rowCount={rowCount}
          loading={loading}
          setLoading={() => {
            handleLoading();
          }}
          id={"prm-table"}
          pageName={"prm"}
          pageOrderName="prm_order"
          onPaginationChange={onPaginationChange}
          actionsCell={actionsCell}
          onSortChange={onSortChange}
          actionsCellWidth={175}
          withBorderTop
          whiteButtons
          defaultPerPage={15}
          defaultColumnVisibilityModel={{ id: false }}
          defaultSearch={initialQuery}
          defaultFilter={initialFilter ? JSON.parse(initialFilter) : undefined}
          defaultSort={[{ field: "id", sort: "desc" }]}
          toolbarActions={toolbarActions}
          handleRowUpdate={handleRowUpdate}
          handleRowUpdateError={handleRowUpdateError}
          getRowClassName={({ row }) =>
            `${row?.new_first_reply ? "new_conversation" : row?.new_reply ? "new_reply" : ""}`
          }
          disableFilter
          filterModel={tableFilter}
          customFilterComponent={
            <LeadsTableFilter
              tableFilter={tableFilter}
              setTableFilter={setTableFilter}
            />
          }
          tableSx={{
            overflow: "hidden",
            // "& .table-toolbar": {
            //     display: "none",
            // },
            "& .new_reply": {
              background: "var(--mantine-color-secondary-0) !important",
            },
            "& .new_conversation": {
              background: "var(--mantine-color-primary-1) !important",
            },
            "& .MuiDataGrid-pinnedColumns": {
              background: "#fff",
              borderTopLeftRadius: 3,
              border: "none",
            },
            "& .MuiDataGrid-pinnedColumnHeaders": {
              background: "#fff",
              border: "none",
            },
          }}
        />
      </Flex>
    </Stack>
  );
};

export default LeadsMainTable;
